import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { GoogleSignInButton } from '@/features/auth/GoogleSignInButton';
import { useSignupMutation } from '@/features/auth/useAuthMutations';
import { signupSchema, type SignupInput } from '@/lib/zodSchemas/auth';
import { ApiError } from '@/lib/api';
import { resolveNextRoute } from './resolveNextRoute';

function formatApiError(error: unknown): string {
  if (error instanceof ApiError) {
    const payload = error.payload;
    if (payload && typeof payload === 'object' && 'message' in payload) {
      const msg = (payload as { message: unknown }).message;
      if (Array.isArray(msg)) return msg.filter((m) => typeof m === 'string').join(' • ');
      if (typeof msg === 'string') {
        if (msg.toLowerCase().includes('throttler') || error.status === 429) {
          return 'Muitas tentativas. Aguarde alguns segundos e tente novamente.';
        }
        return msg;
      }
    }
    if (error.status === 429) return 'Muitas tentativas. Aguarde alguns segundos e tente novamente.';
  }
  return 'Não foi possível criar a conta. Verifique os dados e tente novamente.';
}

export default function SignupPage() {
  const navigate = useNavigate();
  const mutation = useSignupMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupInput>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false as unknown as true,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const data = await mutation.mutateAsync({
        name: values.name,
        email: values.email,
        password: values.password,
      });
      navigate(resolveNextRoute(data.user, null), { replace: true });
    } catch {
      // erro já é exibido via mutation.isError / formatApiError
    }
  });

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold">Criar conta</h1>
      <p className="mt-2 text-sm text-(--color-fg-muted)">
        Leva menos de 3 minutos até o primeiro plantão agendado.
      </p>

      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4" noValidate>
        <FormField id="name" label="Nome completo" error={errors.name?.message} required>
          <Input id="name" autoComplete="name" invalid={!!errors.name} {...register('name')} />
        </FormField>
        <FormField id="email" label="E-mail" error={errors.email?.message} required>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            invalid={!!errors.email}
            {...register('email')}
          />
        </FormField>
        <FormField
          id="password"
          label="Senha"
          hint="Mínimo de 8 caracteres, com letra maiúscula, minúscula e número"
          error={errors.password?.message}
          required
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.password}
            {...register('password')}
          />
        </FormField>
        <FormField
          id="confirmPassword"
          label="Confirmar senha"
          error={errors.confirmPassword?.message}
          required
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.confirmPassword}
            {...register('confirmPassword')}
          />
        </FormField>

        <label className="flex items-start gap-2 text-sm text-(--color-fg-muted)">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-(--color-border)"
            {...register('acceptTerms')}
          />
          <span>
            Li e aceito os{' '}
            <a href="/termos" className="text-(--color-accent) hover:underline">
              Termos de Uso
            </a>{' '}
            e a{' '}
            <a href="/privacidade" className="text-(--color-accent) hover:underline">
              Política de Privacidade
            </a>
            .
          </span>
        </label>
        {errors.acceptTerms ? (
          <p className="text-xs text-(--color-error)" role="alert">
            {errors.acceptTerms.message}
          </p>
        ) : null}

        {mutation.isError ? (
          <p className="text-sm text-(--color-error)" role="alert">
            {formatApiError(mutation.error)}
          </p>
        ) : null}

        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? 'Criando…' : 'Criar conta'}
        </Button>
      </form>

      <GoogleSignInButton />

      <p className="mt-6 text-sm text-(--color-fg-muted)">
        Já tem conta?{' '}
        <Link to="/login" className="text-(--color-accent) hover:underline">
          Entrar
        </Link>
      </p>
    </div>
  );
}
